/**
 * Item-less ("pending") image uploads: start uploading a photo the moment it is
 * picked — before a draft listing exists — then attach the returned R2 keys to
 * the item at submit time via itemsAPI.attachPendingImages.
 */
import { itemsAPI } from '../api/itemsAPI';
import { abortMultipartSession, uploadFileDirectMultipart, isAbortError } from './directR2Upload';

export { isAbortError };
export const pendingResumeKey = (imageId) => `pending.${imageId}`;

/**
 * @param {object} options
 * @param {File|Blob} options.file
 * @param {string|number} options.imageId - stable id of the image entry
 * @param {AbortSignal} [options.signal]
 * @param {boolean} [options.allowVideo] - catalog listings may include videos
 * @param {(label: string, meta?: { percent?: number }) => void} [options.onProgress]
 * @returns {Promise<{ key?: string, deferred?: boolean }>}
 *   { key } when the upload finished (attach at submit),
 *   { deferred: true } when the server has no direct-upload storage — upload at submit instead.
 */
export async function uploadPendingImage({ file, imageId, signal, onProgress, allowVideo }) {
  return uploadFileDirectMultipart({
    api: {
      init: (payload) =>
        itemsAPI.initPendingUpload(allowVideo ? { ...payload, allow_video: true } : payload),
      signParts: (payload) => itemsAPI.signPendingParts(payload),
      complete: (payload) => itemsAPI.completePendingUpload(payload),
      abort: (payload) => itemsAPI.abortPendingUpload(payload),
    },
    file,
    filename: file.name || 'photo.jpg',
    contentType: file.type || 'image/jpeg',
    resumeKey: pendingResumeKey(imageId),
    onProgress,
    signal,
    allowProxyFallback: false,
  });
}

/** Best-effort cancel: stops local resume and aborts the R2 multipart session. */
export async function cancelPendingUpload(imageId) {
  await abortMultipartSession(pendingResumeKey(imageId), (payload) =>
    itemsAPI.abortPendingUpload(payload),
  );
}
